import { useMemo } from 'react';
import { constants } from '@yola/subscription-manager-js';
import featureConfigurator from '../helpers/feature-configurator';
import ecommerceFeatureConfiguration from '../helpers/ecommerce-feature-configuration';
import sortByFeatureId from '../helpers/sort-by-feature-id';
import getFeatureAvailability from '../helpers/get-feature-availability';
import updateHighlightedFeaturesPosition from '../helpers/update-highlighted-features-position';
import getNotAvailableFeatures from '../helpers/get-not-available-features';

const { hostingPackageFeatureNames } = constants;

const defaultHighlightedFeatures = [
  { name: hostingPackageFeatureNames.FREE_DOMAIN_PROMO, highlightDisabledOnly: true },
];

const usePlanFeatures = ({
  paymentPeriod,
  availablePlansData,
  areEcommercePlans,
  featureFlags,
  isB2C,
  highlightedFeatures,
  domainDiscount,
  isTrialDomainDiscountAvailable,
}) =>
  useMemo(() => {
    const { features, plans } = availablePlansData;
    const featureConfigs = areEcommercePlans ? ecommerceFeatureConfiguration : featureConfigurator;
    const notAvailableFeatures = getNotAvailableFeatures(domainDiscount);
    const featuresToHighlight = highlightedFeatures || defaultHighlightedFeatures;

    const availableFeatures = features.reduce((featureList, feature) => {
      if (
        featureConfigs[feature] &&
        getFeatureAvailability(feature, featureFlags, notAvailableFeatures)
      ) {
        const highlightedFeature = featuresToHighlight.find(({ name }) => name === feature);
        const featureConfig = featureConfigs[feature].config(plans, {
          paymentPeriod,
          isB2C,
          highlightDisabledOnly: highlightedFeature?.highlightDisabledOnly,
          isTrialDomainDiscountAvailable,
          featureFlags,
        });
        featureList.push({
          ...featureConfig,
          highlighted:
            featureConfig.highlighted ||
            Boolean(highlightedFeature && !highlightedFeature.highlightDisabledOnly),
        });
      }

      return featureList;
    }, []);

    return updateHighlightedFeaturesPosition(
      sortByFeatureId(availableFeatures, areEcommercePlans),
      featuresToHighlight
    );
  }, [
    paymentPeriod,
    availablePlansData,
    areEcommercePlans,
    featureFlags,
    isB2C,
    highlightedFeatures,
    domainDiscount,
    isTrialDomainDiscountAvailable,
  ]);

export default usePlanFeatures;
